/**
 * Created by katarinababic on 11.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { WeekDayEntryEvent } from '../../Model/Coach/Schedule/WeekDayEntryEvent';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { Activity } from '../../Model/Activity/Activity';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { EventEntry, getEventName, getEventTitle, getEventImage, getEventLink, getEventDurationMinutes } from '../../Model/Coach/Schedule/EventEntry';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useDeleteEvent, useDeleteAllEvents } from '../../Hooks/useEventEntryMutations';
import { StringUtils } from '../../Utils/StringUtils';
import { LinkUtils } from '../../Utils/LinkUtils';
import { WorkoutDayCardEventListItem } from './WorkoutDayCardEventListItem';
import { AlertModal } from '../../Components/AlertModal';
import { Theme } from '../../Theme/Theme';
import { useToast } from '../../Hooks/useToast';
import { useLanguage } from '../../Hooks/useLanguage';
import dayjs from 'dayjs';

type EventObject = Recipe | Activity | ExploreEntry;

const isRecipe = (obj: EventObject | undefined): obj is Recipe => !!obj && 'readyInMinutes' in obj;
const isActivity = (obj: EventObject | undefined): obj is Activity => !!obj && 'trackingKeys' in obj;
const isExploreEntry = (obj: EventObject | undefined): obj is ExploreEntry => !!obj && 'contentType' in obj;

const isVideoBased = (obj: EventObject | undefined): boolean => {
  if (!obj || !isExploreEntry(obj)) return false;
  const type = obj.type;
  if (type === 'workoutTemplate' || type === 'workplace' || type === 'breathing') return false;
  if (type === 'article') return obj.videos.length > 0;
  return type === 'activityWorkout' || type === 'yoga' || type === 'webinar';
};

const getDurationMinutes = (event: EventEntry, obj: EventObject | undefined): number | undefined => {
  if (!obj) return undefined;

  if (isRecipe(obj) && obj.readyInMinutes > 0) {
    return obj.readyInMinutes;
  }

  if (isActivity(obj)) {
    return getEventDurationMinutes(event);
  }

  if (isExploreEntry(obj)) {
    const type = obj.type;
    if (type === 'article' && 'readTimeInMinutes' in obj) {
      const minutes = (obj as any).readTimeInMinutes;
      return minutes > 0 ? minutes : undefined;
    }
    if ((type === 'workoutTemplate' || type === 'activityWorkout') && 'durationInMinutes' in obj) {
      return (obj as any).durationInMinutes || undefined;
    }
    if (type === 'meditation' && 'data' in obj) {
      const duration = (obj as any).data?.get?.('DURATION');
      return duration ? Math.floor(duration / 60) : undefined;
    }
    if (type === 'breathing') {
      return 1;
    }
  }

  return undefined;
};

export type WorkoutDayCardEventExploreEntryProps = {
  entry: WeekDayEntryEvent;
};

export const WorkoutDayCardEventExploreEntry: React.FC<WorkoutDayCardEventExploreEntryProps> = observer(({ entry }) => {
  const { analytics } = useRootStore();
  const toast = useToast();
  const { t } = useTrans();
  const navigate = useNavigate();
  const deleteEvent = useDeleteEvent();
  const deleteAllEvents = useDeleteAllEvents();
  const { language } = useLanguage();

  const eventName = getEventName(entry.event, language);
  const objectName = useLocalized(entry.event.object?.name);
  const object = entry.event.object;

  // Get activity name for activityWorkout type
  const activityNameValue =
    isExploreEntry(object) && object.type === 'activityWorkout' && 'activity' in object
      ? (object as any).activity?.name
      : undefined;
  const activityName = useLocalized(activityNameValue);

  const [showAlert, setShowAlert] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState('');

  const name = eventName || getEventTitle(entry.event) || StringUtils.capitalize(entry.event.objectType || '');
  const title = name ? StringUtils.shortenString(name, 40) : '';

  const isVideoBasedEntry = isVideoBased(object);
  const durationMinutes = getDurationMinutes(entry.event, object);
  const duration = durationMinutes ? (t('globals.timeMin', { value: durationMinutes }) as string) : undefined;

  const eventImage = getEventImage(entry.event);
  const image = eventImage ? { uri: eventImage } : require('../../Assets/Images/logo.png');

  const isActivityWorkout = isExploreEntry(object) && object.type === 'activityWorkout';
  const typeName: string =
    isActivityWorkout && activityName
      ? activityName
      : (t(`coach.schedule.eventTypes.${entry.event.objectType || 'event'}`, {
          defaultValue: entry.event.objectType ?? '',
        }) as string);

  const time = dayjs(entry.startTime).format('LT');
  const subtitle = `${time} • ${typeName}${duration ? ` • ${duration}` : ''}${
    isVideoBasedEntry ? ` • ${t('globals.video', { defaultValue: 'Video' })}` : ''
  }`;

  const handlePress = React.useCallback(() => {
    const objType = entry.event.objectType;
    const objId = entry.event.objectId;
    const link = getEventLink(entry.event);

    // For scheduled activity events, open deep-link to activity logging flow (mobile QR)
    if (objType === 'activity' && link) {
      const parsedLink = LinkUtils.parseLink(link);
      if (LinkUtils.isInternalUrl(parsedLink)) {
        // Strip domain, navigate to internal deep-link route
        const path = parsedLink.replace(/^https?:\/\/*[^/]+/, '');
        navigate(path);
      } else if (LinkUtils.isExternalWebUrl(parsedLink)) {
        window.open(parsedLink, '_blank');
      }
      return;
    }

    if (objId) {
      // Navigate based on content type and log analytics
      switch (objType) {
        case 'recipe':
          analytics.logSelectContent('recipe', objId);
          navigate(`/nutrition/${objId}`);
          break;
        case 'article':
          analytics.logSelectContent('article', objId);
          navigate(`/academy/${objId}`);
          break;
        case 'workoutTemplate':
        case 'activityWorkout':
        case 'yoga':
        case 'webinar':
        case 'workplace':
          analytics.logSelectContent(objType || '', objId);
          navigate(`/move/${objId}`);
          break;
        case 'meditation':
        case 'breathing':
          analytics.logSelectContent(objType || '', objId);
          navigate(`/mental-health/${objId}`);
          break;
        default:
          analytics.logSelectContent(objType || '', objId);
          navigate(`/academy/${objId}`);
      }
      return;
    }

    if (link) {
      const parsedLink = LinkUtils.parseLink(link);
      if (LinkUtils.isInternalUrl(parsedLink)) {
        const path = parsedLink.replace(/^https?:\/\/*[^/]+/, '');
        navigate(path);
      } else if (LinkUtils.isExternalWebUrl(parsedLink)) {
        window.open(parsedLink, '_blank');
      }
    }
  }, [entry, navigate, analytics]);

  // Remove only this event
  const removeEvent = React.useCallback(() => {
    return deleteEvent.mutateAsync(entry.event);
  }, [deleteEvent, entry]);

  // Remove all events (recurring group or single)
  const removeAllEvents = React.useCallback(() => {
    return deleteAllEvents.mutateAsync(entry.event).then(() => {
      analytics.logEvent('event_unscheduled', {
        content_type: entry.event.objectType,
        item_id: entry.event.objectId,
        aiPlanned: entry.event.aiPlanned,
        startTime: entry.event.startTime,
        startDate: entry.event.startDate,
      });
    });
  }, [analytics, deleteAllEvents, entry]);

  const handleLongPress = React.useCallback(() => {
    const titleValue = t('explore.scheduler.removeAlert.titleSession', {
      value: objectName,
    }) as string;
    const messageValue = t('explore.scheduler.removeAlert.messageSession', {
      value: objectName,
    }) as string;
    setAlertTitle(titleValue);
    setAlertMessage(messageValue);
    setShowAlert(true);
  }, [objectName, t]);

  // Format imageSource as CSS background-image
  const bgImage = typeof image === 'object' && 'uri' in image ? `url(${image.uri})` : `url(${image})`;

  return (
    <React.Fragment>
      <WorkoutDayCardEventListItem
        title={title}
        subtitle={subtitle}
        onPress={handlePress}
        onLongPress={handleLongPress}
        imageSource={bgImage}
        showAsDone={!!entry.event.completedAt}
        RightColumnComponent={
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleLongPress();
            }}
            style={{ padding: 4, cursor: 'pointer' }}
            title={t('explore.scheduler.removeAlert.removeOnlyThisEvent') as string}
          >
            <i className="bi bi-trash" style={{ fontSize: 20, color: Theme.Colors.error }} />
          </div>
        }
      />
      <AlertModal
        isOpen={showAlert}
        title={alertTitle}
        body={alertMessage}
        secondaryActionText={t('explore.scheduler.removeAlert.removeAllEvents') as string}
        onSecondaryAction={() => {
          removeAllEvents().then((event) => {
            toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }) as string, 'error');
            window.dispatchEvent(new Event('scheduleChanged'));
          });
          setShowAlert(false);
        }}
        confirmText={t('explore.scheduler.removeAlert.removeOnlyThisEvent') as string}
        cancelText={t('globals.cancel') as string}
        onConfirm={() => {
          removeEvent().then((event) => {
            toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }) as string, 'error');
            window.dispatchEvent(new Event('scheduleChanged'));
          });
          setShowAlert(false);
        }}
        onCancel={() => setShowAlert(false)}
      />
    </React.Fragment>
  );
});
